import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SearchIcon, MapIcon, FileTextIcon, BarChart3Icon, ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
export function WhoWeArePage() {
  return <div className="w-full pt-20 lg:pt-24" data-id="element-649">
      {/* Page Header */}
      <div className="bg-navy py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-650">
        <div className="max-w-7xl mx-auto" data-id="element-651">
          <div className="font-sans text-sm font-semibold text-white/50 uppercase tracking-wider mb-4" data-id="element-652">
            <Link to="/" className="hover:text-gold transition-colors" data-id="element-653">
              Home
            </Link>
            <span className="mx-2 text-gold/50" data-id="element-654">/</span>
            <span className="text-gold" data-id="element-655">Who We Are</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-white font-bold" data-id="element-656">
            Who We Are
          </h1>
          <div className="h-1 w-24 bg-gold mt-6" data-id="element-657"></div>
        </div>
      </div>

      {/* Mission Section */}
      <section className="py-20 bg-cream px-4 sm:px-6 lg:px-8" data-id="element-658">
        <div className="max-w-4xl mx-auto" data-id="element-659">
          <div className="text-center mb-12" data-id="element-660">
            <span className="inline-block font-sans font-bold text-xs uppercase tracking-widest text-gold mb-4" data-id="element-661">
              Our Mission
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-navy font-bold" data-id="element-662">
              Strategic Advisory for the African Century
            </h2>
          </div>
          <div className="space-y-6 font-sans text-lg text-text-muted leading-relaxed" data-id="element-663">
            <p data-id="element-664">
              Founded on the belief that the next frontier of global economic
              growth lies in West Africa, Emma Kay Consulting was established to
              bridge the gap between US corporate ambition and African market
              realities. We recognize that successful market entry requires more
              than just capital—it demands deep cultural intelligence,
              regulatory foresight, and strategic relationships.
            </p>
            <p data-id="element-665">
              Our firm specializes in translating complex policy environments
              into actionable business strategies. By focusing exclusively on
              Nigeria and Ghana, we provide our clients with an unparalleled
              depth of insight and a robust network of local stakeholders, from
              government officials to industry leaders.
            </p>
            <p data-id="element-666">
              Whether you are navigating legislative frameworks, developing
              culturally resonant communications, or implementing rigorous
              monitoring and evaluation systems, our approach is always
              data-driven, ethically grounded, and relentlessly focused on
              delivering measurable impact.
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach / Methodology */}
      <section className="py-20 bg-navy text-white px-4 sm:px-6 lg:px-8" data-id="element-667">
        <div className="max-w-7xl mx-auto" data-id="element-668">
          <div className="text-center mb-16" data-id="element-669">
            <span className="inline-block font-sans font-bold text-xs uppercase tracking-widest text-gold mb-4" data-id="element-670">
              Our Approach
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4" data-id="element-671">
              A Proven Methodology
            </h2>
            <p className="font-sans text-lg text-white/70 max-w-2xl mx-auto" data-id="element-672">
              Every engagement follows our rigorous four-phase framework,
              refined over 15 years of advisory work across West Africa.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-673">
            <motion.div className="relative" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-674">
              <div className="text-gold font-serif text-6xl font-bold opacity-20 mb-4" data-id="element-675">
                01
              </div>
              <SearchIcon className="h-8 w-8 text-gold mb-4" strokeWidth={1.5} data-id="element-676" />
              <h3 className="font-serif text-xl font-semibold mb-3" data-id="element-677">
                Discovery & Assessment
              </h3>
              <p className="font-sans text-sm text-white/70 leading-relaxed" data-id="element-678">
                Deep-dive analysis of your objectives, risk profile, and the
                specific regulatory landscape relevant to your sector in Nigeria
                or Ghana.
              </p>
            </motion.div>

            <motion.div className="relative" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-679">
              <div className="text-gold font-serif text-6xl font-bold opacity-20 mb-4" data-id="element-680">
                02
              </div>
              <MapIcon className="h-8 w-8 text-gold mb-4" strokeWidth={1.5} data-id="element-681" />
              <h3 className="font-serif text-xl font-semibold mb-3" data-id="element-682">
                Strategy Development
              </h3>
              <p className="font-sans text-sm text-white/70 leading-relaxed" data-id="element-683">
                Crafting a tailored market entry or expansion strategy that
                accounts for political dynamics, cultural nuances, and
                competitive positioning.
              </p>
            </motion.div>

            <motion.div className="relative" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-684">
              <div className="text-gold font-serif text-6xl font-bold opacity-20 mb-4" data-id="element-685">
                03
              </div>
              <FileTextIcon className="h-8 w-8 text-gold mb-4" strokeWidth={1.5} data-id="element-686" />
              <h3 className="font-serif text-xl font-semibold mb-3" data-id="element-687">
                Implementation Support
              </h3>
              <p className="font-sans text-sm text-white/70 leading-relaxed" data-id="element-688">
                Hands-on guidance through regulatory processes, stakeholder
                introductions, and communications deployment with our
                on-the-ground teams.
              </p>
            </motion.div>

            <motion.div className="relative" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-689">
              <div className="text-gold font-serif text-6xl font-bold opacity-20 mb-4" data-id="element-690">
                04
              </div>
              <BarChart3Icon className="h-8 w-8 text-gold mb-4" strokeWidth={1.5} data-id="element-691" />
              <h3 className="font-serif text-xl font-semibold mb-3" data-id="element-692">
                Monitoring & Reporting
              </h3>
              <p className="font-sans text-sm text-white/70 leading-relaxed" data-id="element-693">
                Continuous performance tracking with custom dashboards and
                quarterly impact reports that demonstrate measurable ROI to
                stakeholders.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-cream-dark px-4 sm:px-6 lg:px-8" data-id="element-694">
        <div className="max-w-7xl mx-auto" data-id="element-695">
          <div className="text-center mb-16" data-id="element-696">
            <span className="inline-block font-sans font-bold text-xs uppercase tracking-widest text-gold mb-4" data-id="element-697">
              What Guides Us
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-navy font-bold mb-6" data-id="element-698">
              Our Core Values
            </h2>
            <div className="h-1 w-24 bg-gold mx-auto" data-id="element-699"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-700">
            <div className="bg-white p-8 rounded-card shadow-sm border border-gray-100" data-id="element-701">
              <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-702">
                <span className="font-serif text-2xl text-navy font-bold" data-id="element-703">
                  I
                </span>
              </div>
              <h3 className="font-serif text-2xl text-navy font-semibold mb-4" data-id="element-704">
                Integrity
              </h3>
              <p className="font-sans text-text-muted leading-relaxed" data-id="element-705">
                We maintain the highest ethical standards in every engagement
                across borders, ensuring compliance, transparency, and trust in
                all our advisory services.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-sm border border-gray-100" data-id="element-706">
              <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-707">
                <span className="font-serif text-2xl text-navy font-bold" data-id="element-708">
                  E
                </span>
              </div>
              <h3 className="font-serif text-2xl text-navy font-semibold mb-4" data-id="element-709">
                Excellence
              </h3>
              <p className="font-sans text-text-muted leading-relaxed" data-id="element-710">
                Rigorous analysis and world-class deliverables that exceed
                client expectations. We do not compromise on the quality of our
                research or strategic counsel.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-sm border border-gray-100" data-id="element-711">
              <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-712">
                <span className="font-serif text-2xl text-navy font-bold" data-id="element-713">
                  C
                </span>
              </div>
              <h3 className="font-serif text-2xl text-navy font-semibold mb-4" data-id="element-714">
                Cultural Intelligence
              </h3>
              <p className="font-sans text-text-muted leading-relaxed" data-id="element-715">
                Deep understanding of both American corporate environments and
                West African business cultures, allowing us to act as effective
                translators and facilitators.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-20 bg-cream px-4 sm:px-6 lg:px-8" data-id="element-716">
        <div className="max-w-5xl mx-auto" data-id="element-717">
          <div className="text-center mb-16" data-id="element-718">
            <span className="inline-block font-sans font-bold text-xs uppercase tracking-widest text-gold mb-4" data-id="element-719">
              Our Team
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-navy font-bold mb-6" data-id="element-720">
              Leadership
            </h2>
            <div className="h-1 w-24 bg-gold mx-auto" data-id="element-721"></div>
          </div>

          <div className="bg-white rounded-card shadow-sm border border-cream-dark overflow-hidden flex flex-col md:flex-row" data-id="element-722">
            <div className="md:w-2/5 h-80 md:h-auto relative" data-id="element-723">
              <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&q=80" alt="Emma Kay, Founder and Principal Consultant" className="w-full h-full object-cover object-center" data-id="element-724" />
            </div>
            <div className="p-8 md:p-12 md:w-3/5 flex flex-col justify-center" data-id="element-725">
              <h3 className="font-serif text-3xl text-navy font-bold mb-2" data-id="element-726">
                Emma Kay
              </h3>
              <p className="font-sans text-gold font-bold text-sm uppercase tracking-wider mb-6" data-id="element-727">
                Founder & Principal Consultant
              </p>
              <div className="font-sans text-text-muted leading-relaxed space-y-4" data-id="element-728">
                <p data-id="element-729">
                  With over 15 years of experience at the intersection of
                  international development, public policy, and corporate
                  strategy, Emma has advised Fortune 500 companies, NGOs, and
                  government agencies on their West African operations.
                </p>
                <p data-id="element-730">
                  Prior to founding the firm, she led the Africa policy desk at
                  a major Washington D.C. think tank and spent years on the
                  ground in Lagos and Accra developing monitoring and evaluation
                  frameworks for large-scale infrastructure and social impact
                  projects.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-731">
                <span className="px-3 py-1 bg-navy/5 rounded-full font-sans text-xs font-semibold text-navy" data-id="element-732">
                  Public Policy
                </span>
                <span className="px-3 py-1 bg-navy/5 rounded-full font-sans text-xs font-semibold text-navy" data-id="element-733">
                  M&E Frameworks
                </span>
                <span className="px-3 py-1 bg-navy/5 rounded-full font-sans text-xs font-semibold text-navy" data-id="element-734">
                  West Africa
                </span>
                <span className="px-3 py-1 bg-navy/5 rounded-full font-sans text-xs font-semibold text-navy" data-id="element-735">
                  Strategic Comms
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Network */}
      <section className="py-20 bg-cream-dark px-4 sm:px-6 lg:px-8" data-id="element-736">
        <div className="max-w-7xl mx-auto" data-id="element-737">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-738">
            <div data-id="element-739">
              <span className="inline-block font-sans font-bold text-xs uppercase tracking-widest text-gold mb-4" data-id="element-740">
                Our Network
              </span>
              <h2 className="font-serif text-3xl md:text-4xl text-navy font-bold mb-6 leading-tight" data-id="element-741">
                A Global Network, Locally Grounded
              </h2>
              <p className="font-sans text-lg text-text-muted leading-relaxed mb-8" data-id="element-742">
                Our effectiveness stems from deep, trust-based relationships
                built over years of on-the-ground engagement. We maintain active
                partnerships across three continents to deliver comprehensive
                advisory services.
              </p>
              <ul className="space-y-4" data-id="element-743">
                <li className="flex items-start gap-3" data-id="element-744">
                  <CheckCircleIcon className="h-5 w-5 text-gold mt-0.5 flex-shrink-0" data-id="element-745" />
                  <span className="font-sans text-text-muted" data-id="element-746">
                    Senior government officials and regulatory bodies in Abuja,
                    Lagos, and Accra
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-747">
                  <CheckCircleIcon className="h-5 w-5 text-gold mt-0.5 flex-shrink-0" data-id="element-748" />
                  <span className="font-sans text-text-muted" data-id="element-749">
                    Leading international law firms specializing in African
                    trade and investment
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-750">
                  <CheckCircleIcon className="h-5 w-5 text-gold mt-0.5 flex-shrink-0" data-id="element-751" />
                  <span className="font-sans text-text-muted" data-id="element-752">
                    Multilateral development institutions including the World
                    Bank and AfDB
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-753">
                  <CheckCircleIcon className="h-5 w-5 text-gold mt-0.5 flex-shrink-0" data-id="element-754" />
                  <span className="font-sans text-text-muted" data-id="element-755">
                    Local research organizations and data collection agencies
                    across West Africa
                  </span>
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-6" data-id="element-756">
              <div className="bg-white p-6 rounded-card shadow-sm border border-gray-100 text-center" data-id="element-757">
                <div className="font-serif text-3xl text-navy font-bold mb-1" data-id="element-758">
                  Washington
                </div>
                <div className="font-sans text-xs font-semibold text-gold uppercase tracking-wider" data-id="element-759">
                  Headquarters
                </div>
              </div>
              <div className="bg-white p-6 rounded-card shadow-sm border border-gray-100 text-center" data-id="element-760">
                <div className="font-serif text-3xl text-navy font-bold mb-1" data-id="element-761">
                  Lagos
                </div>
                <div className="font-sans text-xs font-semibold text-gold uppercase tracking-wider" data-id="element-762">
                  Nigeria Office
                </div>
              </div>
              <div className="bg-white p-6 rounded-card shadow-sm border border-gray-100 text-center" data-id="element-763">
                <div className="font-serif text-3xl text-navy font-bold mb-1" data-id="element-764">
                  Accra
                </div>
                <div className="font-sans text-xs font-semibold text-gold uppercase tracking-wider" data-id="element-765">
                  Ghana Office
                </div>
              </div>
              <div className="bg-white p-6 rounded-card shadow-sm border border-gray-100 text-center" data-id="element-766">
                <div className="font-serif text-3xl text-navy font-bold mb-1" data-id="element-767">
                  London
                </div>
                <div className="font-sans text-xs font-semibold text-gold uppercase tracking-wider" data-id="element-768">
                  Partner Network
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-navy text-white text-center px-4 sm:px-6 lg:px-8" data-id="element-769">
        <div className="max-w-3xl mx-auto" data-id="element-770">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6" data-id="element-771">
            Ready to Work Together?
          </h2>
          <p className="font-sans text-lg text-white/80 mb-10" data-id="element-772">
            Let us help you navigate the complexities of the West African market
            with confidence and strategic clarity.
          </p>
          <Link to="/inquiry" className="inline-block bg-gold hover:bg-gold-light text-navy font-sans font-bold text-sm uppercase tracking-wide px-8 py-4 rounded-card transition-colors shadow-lg" data-id="element-773">
            Start a Conversation
          </Link>
        </div>
      </section>
    </div>;
}